/**
 * Suggest a canonical WPS pass label for a TXT pass name using the
 * starts-with prefix heuristic decided in the floorsheet-multi-txt-upload
 * PRD (issue 03).
 *
 * Precedence chain (consumers chain this with their own checks):
 *   1. previous-upload mapping for this TXT name        (dialog state)
 *   2. exact identity match against a canonical label   (dialog state)
 *   3. THIS prefix rule                                  (this util)
 *   4. unmapped (`null`)
 *
 * The rule is intentionally minimal: only `H`/`h` and `R`/`r` are mapped.
 * `F`/`C`/`S` are deferred (Fill/Cap/Strip are numbered labels and need a
 * separate number-disambiguation rule we explicitly punted on).
 *
 * Returning `null` means "no suggestion" — the caller should leave the
 * dropdown unmapped so the user picks. Never invent a label.
 */
export function suggestPrefixMapping(
  txtPassName: string,
  canonicalLabels: readonly string[]
): string | null {
  const target = pickTargetFor(txtPassName)
  if (!target) return null
  return findCanonicalLabel(canonicalLabels, target)
}

type CanonicalTarget = 'Hot' | 'Root'

function pickTargetFor(txtPassName: string | null | undefined): CanonicalTarget | null {
  const head = String(txtPassName ?? '').trim().charAt(0).toLowerCase()
  if (head === 'h') return 'Hot'
  if (head === 'r') return 'Root'
  return null
}

/**
 * Pick the best canonical label for a target:
 *   1. Exact case-insensitive match (`Hot` === `Hot`)
 *   2. First label that startsWith the target case-insensitively
 *      (handles custom names like `HotCap`, `RootBead`)
 *   3. `null` — caller leaves Unmapped
 */
function findCanonicalLabel(
  canonicalLabels: readonly string[],
  target: CanonicalTarget
): string | null {
  const targetLc = target.toLowerCase()
  const exact = canonicalLabels.find((label) => label.toLowerCase() === targetLc)
  if (exact) return exact
  const prefixed = canonicalLabels.find((label) => label.toLowerCase().startsWith(targetLc))
  return prefixed ?? null
}
